import { useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { selectClient } from '@client/model';
import { FundingPolicyType } from '@shared';
import { useCheckEligibility } from './use-check-eligibility';
import { useUnderwritingResults } from './use-underwriting-results';
import { getUnderwritingResultForProduct } from '../utils/get-underwriting-result-for-product';

interface UseUnderwritingStatusOptions {
  productName: string;
  fundingPolicyType: FundingPolicyType;
  policyType?: string;
}

export function useUnderwritingStatus({
  productName,
  fundingPolicyType,
  policyType,
}: UseUnderwritingStatusOptions) {
  const client = useSelector(selectClient);
  const clientId = client?.clientId;

  const { handleCheckEligibility, isLoading: isCheckingEligibility } = useCheckEligibility();
  const { data: underwritingData } = useUnderwritingResults(clientId);

  // State for dialogs
  const [showSlideOut, setShowSlideOut] = useState(false);
  const [showQuestionnaireDialog, setShowQuestionnaireDialog] = useState(false);
  const [questionnaireVariant, setQuestionnaireVariant] = useState<
    'underwriting' | 'partner-underwriting'
  >('underwriting');
  const [questionnaireClient, setQuestionnaireClient] = useState<Client | undefined>(undefined);

  // Determine if this is a joint policy
  const isJointPolicy = policyType === 'Joint Life';

  // Get the underwriting result for this specific product
  const underwritingResult = getUnderwritingResultForProduct(productName, underwritingData?.results, isJointPolicy);
  console.log('[useUnderwritingStatus] Product:', productName);
  console.log('[useUnderwritingStatus] Available results:', Object.keys(underwritingData?.results || {}));
  console.log('[useUnderwritingStatus] Matched result:', underwritingResult);
  console.log('[useUnderwritingStatus] resultsText:', underwritingResult?.resultsText);

  const status = underwritingResult?.status;

  // Handler to show questionnaire dialog
  const handleShowQuestionnaireDialog = useCallback(
    (variant: 'underwriting' | 'partner-underwriting') => {
      setQuestionnaireVariant(variant);
      // If partner variant, pass the partner as the client
      if (variant === 'partner-underwriting' && client?.mutableClientPartner) {
        setQuestionnaireClient(client.mutableClientPartner as Client);
      } else {
        setQuestionnaireClient(client);
      }
      setShowQuestionnaireDialog(true);
    },
    [client],
  );

  // Handler to check eligibility
  const handleCheckEligibilityClick = useCallback(() => {
    handleCheckEligibility({
      fundingPolicyType,
      policyType,
      onShowQuestionnaireDialog: handleShowQuestionnaireDialog,
    });
  }, [fundingPolicyType, policyType, handleCheckEligibility, handleShowQuestionnaireDialog]);

  // Handler to view details
  const handleViewDetails = useCallback(() => {
    if (underwritingResult) {
      setShowSlideOut(true);
    }
  }, [underwritingResult]);

  return {
    // Status data
    status,
    underwritingResult: underwritingResult?.resultsText ?? null,
    structuredResult: underwritingResult?.structuredResult ?? null,
    pipelineMetadata: underwritingResult?.pipelineMetadata ?? null,
    hasUnderwritingResult: !!underwritingResult,

    // Loading state
    isLoading: isCheckingEligibility,

    // Dialog states
    showSlideOut,
    setShowSlideOut,
    showQuestionnaireDialog,
    setShowQuestionnaireDialog,
    questionnaireVariant,
    questionnaireClient,

    // Handlers
    handleCheckEligibilityClick,
    handleViewDetails,
  };
}
